import { Photo, type LayoutCtx } from "./_shared";

// ─────────────────────────────────────────────────────────────────────────────
// CoastalLayout — Marvarid's seaside / destination wedding card (w07).
//
// Design notes:
//   * Watercolor seascape backdrop: a horizon of soft blue gradient bands at
//     the top, sand-paper cream in the middle, and a deckle-edged photo in
//     the lower half — like a postcard from Charvak reservoir.
//   * The hero photo sits in the lower portion with a hand-drawn deckle
//     bottom (SVG mask + wavy lower border) so it reads as paper torn at
//     the tide line. Sea-foam wash overlays the photo.
//   * Names in Tenor Sans, light weight, generous letterspacing in deep
//     teal-ink. Tiny pearl trio (3 dots like beads on string) sits between
//     the eyebrow and the names — Marvarid (Pearl) signature.
//   * Eyebrow "BY THE WATER · CHARVAK · 2026" sits up top in sea-foam.
//   * Date + venue at the bottom in sea-foam tone.
// ─────────────────────────────────────────────────────────────────────────────

let cardSeq = 0;
const nextCardId = () => `marvarid-card-${++cardSeq}`;

export function CoastalLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
  photos,
}: LayoutCtx) {
  const id = nextCardId();
  const heroSrc = photos[0] || photo;

  return (
    <div className={`${id}-card marvarid-card`} style={{ position: "absolute", inset: 0, overflow: "hidden", background: bg }}>
      <style>{`
        @keyframes ${id}-shimmer {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 0.78; }
        }
        @keyframes ${id}-driftWave {
          0%   { transform: translateX(-6%); }
          50%  { transform: translateX(2%);  }
          100% { transform: translateX(-6%); }
        }
        .${id}-shimmer  { animation: ${id}-shimmer 6s ease-in-out infinite; }
        .${id}-driftA   { animation: ${id}-driftWave 7.5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .${id}-shimmer, .${id}-driftA { animation: none !important; }
        }
        @media (max-width: 768px) {
          .${id}-card .${id}-pearls { transform: scale(0.85); }
        }
      `}</style>

      {/* ── Watercolor seascape backdrop (top half) ─────────────────── */}
      <svg
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      >
        <defs>
          {/* Sky → sea horizon: pale sand → milky blue → sea-foam */}
          <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={bg} stopOpacity="1" />
            <stop offset="35%" stopColor={accent} stopOpacity="0.10" />
            <stop offset="55%" stopColor={accent} stopOpacity="0.22" />
            <stop offset="75%" stopColor={accent} stopOpacity="0.10" />
            <stop offset="100%" stopColor={bg} stopOpacity="1" />
          </linearGradient>
          {/* Painted edge filter for the watercolor bands */}
          <filter id={`${id}-paint`} x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="2" seed="7" />
            <feDisplacementMap in="SourceGraphic" scale="1.6" />
          </filter>
          {/* Deckle-edge mask for hero photo: wavy bottom */}
          <clipPath id={`${id}-deckle`} clipPathUnits="userSpaceOnUse">
            <path d="M 8 92 L 192 92 L 192 232 Q 178 226 168 230 T 140 232 T 110 228 T 78 232 T 50 230 T 22 232 L 8 230 Z" />
          </clipPath>
        </defs>

        {/* Sky/sea wash */}
        <rect width="200" height="280" fill={`url(#${id}-sky)`} />

        {/* Three watercolor horizon bands (above the photo) */}
        <g filter={`url(#${id}-paint)`} className={`${id}-shimmer`}>
          <rect x="0"  y="58"  width="200" height="6"  fill={accent} fillOpacity="0.18" />
          <rect x="0"  y="70"  width="200" height="3"  fill={accent} fillOpacity="0.12" />
          <rect x="0"  y="80"  width="200" height="2"  fill={accent} fillOpacity="0.10" />
        </g>

        {/* Faint sun spot — the morning light over the reservoir */}
        <circle cx="156" cy="40" r="14" fill={accent} fillOpacity="0.10" />
        <circle cx="156" cy="40" r="7"  fill={accent} fillOpacity="0.16" />
      </svg>

      {/* ── Hero photo with deckle/torn-paper bottom ───────────────── */}
      <svg
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      >
        <g clipPath={`url(#${id}-deckle)`}>
          <foreignObject x="8" y="92" width="184" height="142">
            <div style={{ width: "100%", height: "100%", position: "relative" }}>
              <Photo
                src={heroSrc}
                style={{ position: "absolute", inset: 0, opacity: 0.94 }}
              />
              {/* Sea-foam wash over the photo */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(180deg, ${accent}10 0%, ${accent}00 35%, ${accent}1f 100%)`,
                  mixBlendMode: "multiply",
                }}
              />
            </div>
          </foreignObject>
        </g>
        {/* Hairline along the deckle edge */}
        <path
          d="M 8 92 L 192 92 L 192 232 Q 178 226 168 230 T 140 232 T 110 228 T 78 232 T 50 230 T 22 232 L 8 230 Z"
          fill="none"
          stroke={accent}
          strokeOpacity="0.55"
          strokeWidth="0.6"
        />

        {/* Tiny rolling wave just under the photo bottom */}
        <g className={`${id}-driftA`} opacity="0.55">
          <path
            d="M 0 246 Q 25 240 50 246 T 100 246 T 150 246 T 200 246"
            fill="none"
            stroke={accent}
            strokeWidth="0.7"
          />
          <path
            d="M 0 252 Q 25 248 50 252 T 100 252 T 150 252 T 200 252"
            fill="none"
            stroke={accent}
            strokeOpacity="0.55"
            strokeWidth="0.5"
          />
        </g>
      </svg>

      {/* ── Eyebrow (top) ───────────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: "6%",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.42em",
          color: accent,
        }}
      >
        BY THE WATER · CHARVAK · 2026
      </div>

      {/* ── Pearl trio (three beads) ─────────────────────────────── */}
      <div
        className={`${id}-pearls`}
        style={{
          position: "absolute",
          top: "12%",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 4,
          alignItems: "center",
        }}
        aria-hidden
      >
        {[0, 1, 2].map((i) => (
          <svg key={i} width={i === 1 ? 8 : 6} height={i === 1 ? 8 : 6} viewBox="0 0 10 10">
            <defs>
              <radialGradient id={`${id}-pearl-${i}`} cx="35%" cy="35%" r="60%">
                <stop offset="0%"  stopColor="#FFFFFF" stopOpacity="0.95" />
                <stop offset="55%" stopColor={bg} stopOpacity="1" />
                <stop offset="100%" stopColor={accent} stopOpacity="0.85" />
              </radialGradient>
            </defs>
            <circle cx="5" cy="5" r="4.4" fill={`url(#${id}-pearl-${i})`} stroke={accent} strokeOpacity="0.5" strokeWidth="0.4" />
          </svg>
        ))}
      </div>

      {/* ── Names (between eyebrow and photo) ──────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: 0,
          right: 0,
          textAlign: "center",
          padding: "0 8%",
        }}
      >
        <div
          style={{
            fontFamily: font,
            fontSize: 22,
            fontWeight: 300,
            color: ink,
            letterSpacing: "0.18em",
            lineHeight: 1.05,
            textTransform: "uppercase",
          }}
        >
          {names}
        </div>
        {/* Sea-foam underline (short) */}
        <svg
          width="38%"
          height="6"
          viewBox="0 0 100 6"
          preserveAspectRatio="none"
          style={{ display: "block", margin: "5px auto 0" }}
        >
          <path d="M 2 3 Q 20 1 40 3 T 80 3 T 98 3" stroke={accent} strokeWidth="0.9" fill="none" strokeLinecap="round" />
        </svg>
      </div>

      {/* ── Date + venue (bottom in sea-foam) ─────────────────────── */}
      <div
        style={{
          position: "absolute",
          bottom: "5%",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.34em",
          color: accent,
        }}
      >
        {date} · {venue.toUpperCase()}
      </div>
    </div>
  );
}
